import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { projectContactListAPI } from "../../../Api/ProjectApi";
import { ContactEmployeePermission, ContactEmployeePermissionRequestItem } from "../../../Controllers/Types/EmployeeInterface";

export const CONTACT_PERMISSION_TYPES = ["Employee", "Professional", "Client"] as const;

export interface ContactPermissionItem {
    contactId: number;
    contactType: string;
    contactSubType: string;
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    mobile: string;
    landline?: string | null;
    address?: string | null;
    city?: string | null;
    post_code?: string | null;
    profile_image?: string | null;
    role_name?: string | null;
    icChecked?: boolean;
    [key: string]: unknown;
}

interface Props {
    selectedTypes: string[];
    onChange: (types: string[]) => void;
    selectedContacts?: ContactPermissionItem[];
    onSelectedContactsChange?: (contacts: ContactPermissionItem[]) => void;
    initialPermissions?: ContactEmployeePermission[] | null;
    contactsData?: ContactPermissionItem[] | null;
}

const contactKey = (contact: ContactPermissionItem) =>
    `${contact.contactType}-${contact.contactId}-${contact.id}`;

export const createContactPermissionRequest = (
    selectedContacts: ContactPermissionItem[]
): ContactEmployeePermissionRequestItem[] =>
    CONTACT_PERMISSION_TYPES.map((type) => {
        const contactIds = Array.from(new Set(
            selectedContacts
                .filter((contact) => contact.contactType?.toLowerCase() === type.toLowerCase())
                .map((contact) => Number(contact.contactId))
                .filter(Number.isFinite)
        ));

        return {
            type,
            allow_contact: contactIds.length > 0 ? 1 : 0,
            contact_ids: contactIds,
        };
    });

// This feature developed by Dinesh - grouped contact access selector.
const ContactPermissionSelector: React.FC<Props> = ({
    selectedTypes,
    onChange,
    selectedContacts = [],
    onSelectedContactsChange,
    initialPermissions,
    contactsData,
}) => {
    const [contacts, setContacts] = useState<ContactPermissionItem[]>([]);
    const [expandedTypes, setExpandedTypes] = useState<string[]>(["Employee"]);
    const [searchKey, setSearchKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const initialSelectionApplied = useRef(false);

    useEffect(() => {
        if (contactsData !== undefined) {
            if (Array.isArray(contactsData)) {
                setContacts(contactsData);
                setLoading(false);
                setLoadError("");
            } else {
                setLoading(true);
            }
            return;
        }

        let active = true;

        const loadContacts = async () => {
            setLoading(true);
            setLoadError("");
            try {
                const response = await projectContactListAPI();
                if (!active) return;

                const list = Array.isArray(response.data)
                    ? (response.data as ContactPermissionItem[])
                    : [];
                setContacts(list);
            } catch (error) {
                if (active) setLoadError("Unable to load contacts. Please try again.");
            } finally {
                if (active) setLoading(false);
            }
        };

        loadContacts();
        return () => { active = false; };
    }, [contactsData]);

    useEffect(() => {
        if (
            initialSelectionApplied.current ||
            contacts.length === 0 ||
            initialPermissions === null ||
            !onSelectedContactsChange
        ) return;

        const hasPermissionResponse = initialPermissions !== undefined;
        const permissionList = Array.isArray(initialPermissions) ? initialPermissions : [];
        const permittedContactIds = new Set(
            permissionList.flatMap((permission) =>
                Array.isArray(permission.contact_list) ? permission.contact_list.map(Number) : []
            )
        );

        const initiallySelected = contacts.filter((contact) =>
            hasPermissionResponse
                ? permittedContactIds.has(Number(contact.contactId))
                : Boolean(contact.icChecked)
        );
        const initialKeys = new Set(initiallySelected.map(contactKey));
        const completelySelectedTypes = CONTACT_PERMISSION_TYPES.filter((type) => {
            const group = contacts.filter(
                (contact) => contact.contactType?.toLowerCase() === type.toLowerCase()
            );
            return group.length > 0 && group.every((contact) => initialKeys.has(contactKey(contact)));
        });

        initialSelectionApplied.current = true;
        onSelectedContactsChange(initiallySelected);
        onChange(completelySelectedTypes);
    }, [contacts, initialPermissions, onChange, onSelectedContactsChange]);

    const selectedKeys = useMemo(
        () => new Set(selectedContacts.map(contactKey)),
        [selectedContacts]
    );

    const contactsByType = useMemo(() => {
        const query = searchKey.trim().toLowerCase();
        return CONTACT_PERMISSION_TYPES.reduce<Record<string, ContactPermissionItem[]>>(
            (groups, contactType) => {
                groups[contactType] = contacts.filter((contact) => {
                    if (contact.contactType?.toLowerCase() !== contactType.toLowerCase()) return false;
                    if (!query) return true;
                    return [
                        contact.first_name,
                        contact.last_name,
                        contact.email,
                        contact.mobile,
                        contact.contactSubType,
                        contact.role_name,
                    ].some((value) => String(value ?? "").toLowerCase().includes(query));
                });
                return groups;
            },
            {}
        );
    }, [contacts, searchKey]);

    const allContactsForType = (contactType: string) =>
        contacts.filter(
            (contact) => contact.contactType?.toLowerCase() === contactType.toLowerCase()
        );

    const setTypeSelected = (contactType: string, checked: boolean) => {
        onChange(
            checked
                ? Array.from(new Set([...selectedTypes, contactType]))
                : selectedTypes.filter((type) => type !== contactType)
        );

        if (!onSelectedContactsChange) return;
        const groupKeys = new Set(allContactsForType(contactType).map(contactKey));
        onSelectedContactsChange(
            checked
                ? [
                    ...selectedContacts,
                    ...allContactsForType(contactType).filter(
                        (contact) => !selectedKeys.has(contactKey(contact))
                    ),
                ]
                : selectedContacts.filter((contact) => !groupKeys.has(contactKey(contact)))
        );
    };

    const setContactSelected = (contact: ContactPermissionItem, checked: boolean) => {
        if (!onSelectedContactsChange) return;
        const key = contactKey(contact);
        const nextContacts = checked
            ? [...selectedContacts, contact]
            : selectedContacts.filter((item) => contactKey(item) !== key);
        onSelectedContactsChange(nextContacts);

        const group = allContactsForType(contact.contactType);
        const nextKeys = new Set(nextContacts.map(contactKey));
        const isWholeGroupSelected =
            group.length > 0 && group.every((item) => nextKeys.has(contactKey(item)));
        onChange(
            isWholeGroupSelected
                ? Array.from(new Set([...selectedTypes, contact.contactType]))
                : selectedTypes.filter((type) => type !== contact.contactType)
        );
    };

    const toggleExpanded = (contactType: string) => {
        setExpandedTypes((current) =>
            current.includes(contactType)
                ? current.filter((type) => type !== contactType)
                : [...current, contactType]
        );
    };

    return (
        <div className="contact_permission_selector">
            <div className="contact_permission_intro">
                <i className="bi bi-shield-check"></i>
                <div>
                    <h6>Contact access</h6>
                    <p>Select the contacts this employee can access.</p>
                </div>
            </div>

            <div className="contact_permission_search">
                <i className="bi bi-search"></i>
                <InputText
                    value={searchKey}
                    onChange={(event) => setSearchKey(event.target.value)}
                    placeholder="Search contacts"
                    aria-label="Search contacts"
                />
                {searchKey && (
                    <button type="button" onClick={() => setSearchKey("")} aria-label="Clear search">
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}
            </div>

            {loading && <div className="contact_permission_message"><i className="bi bi-arrow-repeat"></i> Loading contacts...</div>}
            {!loading && loadError && <div className="contact_permission_message error">{loadError}</div>}

            {!loading && !loadError && (
                <div className="contact_permission_groups">
                    {CONTACT_PERMISSION_TYPES.map((contactType) => {
                        const groupContacts = contactsByType[contactType] || [];
                        const fullGroup = allContactsForType(contactType);
                        const allSelected =
                            fullGroup.length > 0 &&
                            fullGroup.every((contact) => selectedKeys.has(contactKey(contact)));
                        const expanded = expandedTypes.includes(contactType);

                        return (
                            <div className={`contact_permission_group_wrapper ${allSelected ? "selected" : ""}`} key={contactType}>
                                <div className="contact_permission_group" role="button" tabIndex={0} onClick={() => toggleExpanded(contactType)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") toggleExpanded(contactType); }}>
                                    <span className="contact_permission_icon"><i className={`bi ${contactType === "Employee" ? "bi-people" : contactType === "Professional" ? "bi-briefcase" : "bi-person-vcard"}`}></i></span>
                                    <span className="contact_permission_text">
                                        <strong>{contactType}</strong>
                                        <small>{fullGroup.length} contact{fullGroup.length === 1 ? "" : "s"}</small>
                                    </span>
                                    <label className="contact_permission_select_all" onClick={(event) => event.stopPropagation()}>
                                        <span>Select all</span>
                                        <Checkbox
                                            inputId={`permission-${contactType}`}
                                            checked={allSelected}
                                            onChange={(event) => setTypeSelected(contactType, Boolean(event.checked))}
                                        />
                                    </label>
                                    <i className={`bi bi-chevron-${expanded ? "up" : "down"} contact_permission_chevron`}></i>
                                </div>

                                {expanded && (
                                    <div className="contact_permission_contact_list">
                                        {groupContacts.length === 0 ? (
                                            <div className="contact_permission_empty">{searchKey ? "No matching contacts found." : `No ${contactType.toLowerCase()} contacts found.`}</div>
                                        ) : groupContacts.map((contact) => {
                                            const checked = selectedKeys.has(contactKey(contact));
                                            const fullName = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() || "Unnamed contact";
                                            return (
                                                <label className={`contact_permission_contact ${checked ? "selected" : ""}`} key={contactKey(contact)}>
                                                    <img src={contact.profile_image || "/assets/images/user.png"} alt="" />
                                                    <span className="contact_permission_contact_details">
                                                        <strong>{fullName}</strong>
                                                        <small>{contact.role_name || contact.contactSubType || contact.email || contact.mobile || "---"}</small>
                                                    </span>
                                                    <Checkbox
                                                        inputId={`contact-permission-${contactKey(contact)}`}
                                                        checked={checked}
                                                        onChange={(event) => setContactSelected(contact, Boolean(event.checked))}
                                                    />
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ContactPermissionSelector;
